import type { FinanceCommandCenterPayload } from './financeCommandCenter.types';
import type { BankConnection } from '@/services/bankingClient';
import { formatRecordedAmount } from './financeInvoices.types';

export type CompanyMetric =
  | 'bankBalance'
  | 'revenue'
  | 'grossProfit'
  | 'netIncome'
  | 'receivableBalance'
  | 'payableBalance';
export type CompanyAccounting = NonNullable<FinanceCommandCenterPayload['accounting']> & {
  connectionId?: string | null;
  companyName?: string | null;
};

export const COMPANY_METRICS: ReadonlyArray<{ key: CompanyMetric; label: string; report: string }> =
  [
    { key: 'bankBalance', label: 'Book cash', report: 'balance-sheet' },
    { key: 'revenue', label: 'Revenue', report: 'profit-loss' },
    { key: 'netIncome', label: 'Net income', report: 'profit-loss' },
    { key: 'receivableBalance', label: 'Net receivables', report: 'balance-sheet' },
    { key: 'payableBalance', label: 'Net payables', report: 'balance-sheet' },
  ];

export function companyMetricScope(
  data: CompanyAccounting | null | undefined,
  metric: CompanyMetric,
) {
  const isProfitLoss = metric === 'revenue' || metric === 'grossProfit' || metric === 'netIncome';
  const profitLoss = data?.reportScopes?.profitLoss;
  const balanceSheet = data?.reportScopes?.balanceSheet;
  return {
    report: isProfitLoss ? 'profit-loss' : 'balance-sheet',
    reportLabel: isProfitLoss ? 'Profit & Loss' : 'Balance Sheet',
    startDate: isProfitLoss ? (profitLoss?.startDate ?? null) : (balanceSheet?.startDate ?? null),
    endDate: isProfitLoss ? (profitLoss?.endDate ?? null) : (balanceSheet?.asOfDate ?? null),
    basis: (isProfitLoss ? profitLoss?.basis : balanceSheet?.basis) ?? null,
    currency: (isProfitLoss ? profitLoss?.currency : balanceSheet?.currency) ?? null,
    statementStartLabel: !isProfitLoss
      ? `Statement start ${balanceSheet?.startDate || 'unreported'}`
      : null,
    dateLabel: isProfitLoss
      ? profitLoss?.startDate || profitLoss?.endDate
        ? `${profitLoss.startDate || 'Start unreported'} to ${profitLoss.endDate || 'end unreported'}`
        : 'Returned period unreported'
      : balanceSheet?.asOfDate
        ? `As of ${balanceSheet.asOfDate}`
        : 'Returned as-of date unreported',
  };
}

export function companyReportScopeWarnings(data: CompanyAccounting): string[] {
  const profitLoss = companyMetricScope(data, 'revenue');
  const balanceSheet = companyMetricScope(data, 'bankBalance');
  const warnings: string[] = [];
  if (
    !profitLoss.startDate ||
    !profitLoss.endDate ||
    !balanceSheet.startDate ||
    !balanceSheet.endDate
  )
    warnings.push(
      'Returned report dates are incomplete. Requested dates do not establish the source period.',
    );
  if (
    (profitLoss.startDate && profitLoss.startDate !== data.periodStart) ||
    (profitLoss.endDate && profitLoss.endDate !== data.periodEnd) ||
    (balanceSheet.endDate && balanceSheet.endDate !== data.periodEnd) ||
    (balanceSheet.startDate && balanceSheet.startDate !== data.periodStart) ||
    (profitLoss.basis && profitLoss.basis !== data.basis) ||
    (balanceSheet.basis && balanceSheet.basis !== data.basis)
  )
    warnings.push(
      'QuickBooks returned a different scope from the requested dates or basis. Source labels below show the returned scope.',
    );
  if (
    (profitLoss.endDate && balanceSheet.endDate && profitLoss.endDate !== balanceSheet.endDate) ||
    (profitLoss.basis && balanceSheet.basis && profitLoss.basis !== balanceSheet.basis) ||
    (profitLoss.currency && balanceSheet.currency && profitLoss.currency !== balanceSheet.currency)
  )
    warnings.push(
      'Profit & Loss and Balance Sheet scopes differ. Keep these measures separate; do not compare or combine them as one period, basis or currency.',
    );
  return warnings;
}

/** A new read can reproduce only a complete returned scope, never a requested-date substitute. */
export function companyReportHref(data: CompanyAccounting, metric: CompanyMetric): string | null {
  const source = companyMetricScope(data, metric);
  if (!source.startDate || !source.endDate || !['Accrual', 'Cash'].includes(source.basis ?? ''))
    return null;
  const params = new URLSearchParams({
    report: source.report,
    startDate: source.startDate,
    endDate: source.endDate,
    basis: source.basis!,
    metric,
  });
  if (data.connectionId) params.set('connectionId', data.connectionId);
  return `/accounting/reports?${params}`;
}

export function formatCompanyMoney(
  value: string | null | undefined,
  currency: string | null | undefined,
): string {
  if (!value || !/^-?\d+(?:\.\d{1,2})?$/.test(value)) return '—';
  if (!currency) return formatRecordedAmount(value);
  try {
    const [whole, fraction = ''] = value.split('.');
    const integer = BigInt(whole!);
    const signedInteger = integer === 0n && value.startsWith('-') ? -0 : integer;
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
      .formatToParts(signedInteger)
      .map((part) => (part.type === 'fraction' ? fraction.padEnd(2, '0') : part.value))
      .join('');
  } catch {
    return `${formatRecordedAmount(value)} ${currency}`;
  }
}

export function plaidTransactionCoverage(connections: BankConnection[]) {
  const rows = connections.filter(
    (row) => row.provider.toLowerCase() === 'plaid' && !row.simulated,
  );
  return {
    count: rows.reduce((sum, row) => sum + row.counts.transactions, 0),
    oldestSuccessfulIngestion:
      rows.length && rows.every((row) => row.lastSyncedAt)
        ? rows.map((row) => row.lastSyncedAt!).sort()[0]
        : null,
    connections: rows.length,
    degraded: rows.some((row) => row.status !== 'ACTIVE' || Boolean(row.lastErrorCode)),
  };
}
